import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../config/api';
import LoadingIndicator from '../Components/LoadingIndicator';

const Dashboard = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    users: {
      admin: 0,
      user: 0,
      contentManager: 0
    },
    content: {
      active: 0
    }
  });

  useEffect(() => {
    fetchDashboardCounts();
  }, []);

  const fetchDashboardCounts = async () => {
    if (!API_URL) {
      toast.error('API URL not configured.');
      return;
    }
    setIsLoading(true);
    try {
      const response = await axios.post(
        API_URL,
        { action: 'getDashboardCounts' },
        { headers: { 'Content-Type': 'application/json' } }
      );

      const data = response.data;
      if (data) {
        setDashboardData({
          users: data.users || { admin: 0, user: 0, contentManager: 0 },
          content: data.content || { active: 0 }
        });
      }
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to load dashboard data';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const getTotalUsers = () => {
    return (dashboardData.users.admin || 0) + 
           (dashboardData.users.user || 0) + 
           (dashboardData.users.contentManager || 0);
  };

  return (
    <div className="employee-form">
      <div className="content-header">
        <div className="row align-items-center">
          <div className="col-sm-12">
            <h1>Dashboard</h1>
            <p>Overview of platform statistics</p>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="card employee-content-card">
          <div className="card-body">
            <div className="employee-loading">
              <LoadingIndicator size={120} />
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Main Statistics Cards */}
          <div className="row" style={{ marginBottom: '30px' }}>
            {/* Total Users Card */}
            <div className="col-xl-3 col-md-6 mb-4">
              <div className="dashboard-card total">
                <div className="dashboard-card-body">
                  <div className="dashboard-card-content">
                    <div className="dashboard-card-text">
                      <span className="dashboard-card-title">Total Users</span>
                      <span className="dashboard-card-value">{getTotalUsers()}</span>
                    </div>
                    <div className="dashboard-card-icon">
                      <i className="fas fa-users"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Active Content Card */}
            <div className="col-xl-3 col-md-6 mb-4">
              <div className="dashboard-card content">
                <div className="dashboard-card-body">
                  <div className="dashboard-card-content">
                    <div className="dashboard-card-text">
                      <span className="dashboard-card-title">Active Lead Posts</span>
                      <span className="dashboard-card-value">{dashboardData.content.active}</span>
                    </div>
                    <div className="dashboard-card-icon">
                      <i className="fas fa-file-alt"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* User Statistics Section */}
          <div className="row">
            <div className="col-12" style={{ marginBottom: '20px' }}>
              <h5 style={{ 
                fontSize: '20px', 
                fontWeight: '600',
                color: '#1f2937',
                marginLeft: '12px'
              }}>
                User Statistics
              </h5>
            </div>

            {/* Admin Users Card */}
            <div className="col-xl-4 col-md-6 mb-4">
              <div className="dashboard-card admin">
                <div className="dashboard-card-body">
                  <div className="dashboard-card-content">
                    <div className="dashboard-card-text">
                      <span className="dashboard-card-title">Administrators</span>
                      <span className="dashboard-card-value">{dashboardData.users.admin}</span>
                      <span className="dashboard-card-subtitle">Admin Access</span>
                    </div>
                    <div className="dashboard-card-icon">
                      <i className="fas fa-user-shield"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Lead Managers Card */}
            <div className="col-xl-4 col-md-6 mb-4">
              <div className="dashboard-card manager">
                <div className="dashboard-card-body">
                  <div className="dashboard-card-content">
                    <div className="dashboard-card-text">
                      <span className="dashboard-card-title">Lead Managers</span>
                      <span className="dashboard-card-value">{dashboardData.users.contentManager}</span>
                      <span className="dashboard-card-subtitle">Content Management</span>
                    </div>
                    <div className="dashboard-card-icon">
                      <i className="fas fa-user-tie"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Platform Users Card */}
            <div className="col-xl-4 col-md-6 mb-4">
              <div className="dashboard-card users">
                <div className="dashboard-card-body">
                  <div className="dashboard-card-content">
                    <div className="dashboard-card-text">
                      <span className="dashboard-card-title">Platform Users</span>
                      <span className="dashboard-card-value">{dashboardData.users.user}</span>
                      <span className="dashboard-card-subtitle">Standard Access</span>
                    </div>
                    <div className="dashboard-card-icon">
                      <i className="fas fa-users"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <style jsx>{`
        .dashboard-card {
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          overflow: hidden;
          height: 100%;
        }

        .dashboard-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
        }

        .dashboard-card.total {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }

        .dashboard-card.content {
          background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
        }

        .dashboard-card.admin {
          background: #ffffff;
          border: 2px solid #fee2e2;
        }

        .dashboard-card.admin .dashboard-card-title {
          color: #dc2626;
        }

        .dashboard-card.admin .dashboard-card-value {
          color: #1f2937;
        }

        .dashboard-card.admin .dashboard-card-subtitle {
          color: #ef4444;
        }

        .dashboard-card.admin .dashboard-card-icon {
          color: #dc2626;
        }

        .dashboard-card.manager {
          background: #ffffff;
          border: 2px solid #fed7aa;
        }

        .dashboard-card.manager .dashboard-card-title {
          color: #d97706;
        }

        .dashboard-card.manager .dashboard-card-value {
          color: #1f2937;
        }

        .dashboard-card.manager .dashboard-card-subtitle {
          color: #f59e0b;
        }

        .dashboard-card.manager .dashboard-card-icon {
          color: #d97706;
        }

        .dashboard-card.users {
          background: #ffffff;
          border: 2px solid #d1fae5;
        }

        .dashboard-card.users .dashboard-card-title {
          color: #059669;
        }

        .dashboard-card.users .dashboard-card-value {
          color: #1f2937;
        }

        .dashboard-card.users .dashboard-card-subtitle {
          color: #10b981;
        }

        .dashboard-card.users .dashboard-card-icon {
          color: #059669;
        }

        .dashboard-card-body {
          padding: 24px;
        }

        .dashboard-card-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .dashboard-card-text {
          display: flex;
          flex-direction: column;
        }

        .dashboard-card-title {
          color: rgba(255, 255, 255, 0.9);
          font-size: 14px;
          font-weight: 500;
          margin-bottom: 8px;
        }

        .dashboard-card-value {
          color: #ffffff;
          font-size: 36px;
          font-weight: 700;
          line-height: 1;
        }

        .dashboard-card-subtitle {
          color: rgba(255, 255, 255, 0.8);
          font-size: 12px;
          font-weight: 400;
          margin-top: 6px;
        }

        .dashboard-card-icon {
          font-size: 48px;
          color: rgba(255, 255, 255, 0.3);
        }

        @media (max-width: 768px) {
          .dashboard-card-value {
            font-size: 28px;
          }

          .dashboard-card-icon {
            font-size: 36px;
          }
        }
      `}</style>
    </div>
  );
};

export default Dashboard;